import React, { useMemo } from 'react';
import { MiniLoader } from '@croquiscom-pvt/zds';
import * as Sentry from '@sentry/nextjs';
import parse, { domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';

import { CUSTOM_TAGS_NAME, CUSTOM_TAGS_VALUE } from '@domains/pdp-common';

import { LazyIframe } from '../components/LazyIframe';
import { TableTemplate } from '../components/TableTemplate';
import { whiteScriptList } from '../constants/description.constant';
import {
  adjustAspectRatio,
  checkIsSwiperLink,
  checkIsValidDomAttribute,
  checkLinkAttribute,
  checkNeedAdjustSource,
  checkScriptAttribute,
  checkVimeoSource,
  parseAttributes,
  prependUrlToHref,
  prependUrlToImgSrc,
  removeCssFontSize,
  removeCssImportantSyntax,
  setAttrAlt,
  setVideoAttr,
  shouldLoadLazyIframe,
} from '../utils/html-parser';

interface ParseDescriptionProps {
  shopId: string;
  productId: string;
  shop_url: string;
  description: string;
  isLazyImage?: boolean;
}

const ERROR_RATE = 0.001;

const loaderStyle: React.CSSProperties = {
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  background: '#ffffff',
};

export function useParseDescription({
  shopId,
  productId,
  shop_url,
  description,
  isLazyImage = true,
}: ParseDescriptionProps) {
  const parseDescription = useMemo(() => {
    const wrap_description = `<div>${description}</div>`;

    const options: HTMLReactParserOptions = {
      replace: (_domNode) => {
        if (_domNode instanceof Element && _domNode.attribs) {
          let domNode = _domNode as Element;

          const checkExcludeLink = checkLinkAttribute(domNode.name) && !checkIsSwiperLink(domNode);

          if (!checkIsValidDomAttribute(domNode.name) || checkExcludeLink) {
            if (domNode.children.length > 0) {
              return <>{domToReact(domNode.children, options)}</>;
            }
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <></>;
          }

          domNode.attribs = parseAttributes(domNode.attribs);
          if (!domNode.attribs.style) {
            domNode.attribs.style = '';
          }

          domNode = removeCssFontSize(domNode);

          domNode = removeCssImportantSyntax(domNode);

          if (domNode.name === 'a') {
            domNode = prependUrlToHref(domNode, shop_url);
          }

          if (domNode.name === 'video') {
            domNode = setVideoAttr(domNode);
          }

          if (domNode.name === 'img') {
            if (isLazyImage) {
              domNode = prependUrlToImgSrc(domNode, shop_url);
            }
            domNode = setAttrAlt(domNode);

            const { class: className, referrerpolicy, ...rest } = domNode.attribs;

            const imgProps = {
              ...rest,
              className,
              referrerPolicy: referrerpolicy as React.HTMLAttributeReferrerPolicy, // Ensure correct type
            };

            return (
              <div className='pdp-image'>
                {isLazyImage && <MiniLoader style={loaderStyle} />}
                <img {...imgProps} style={cssStringToJson(domNode.attribs.style)} alt={domNode.attribs.alt} />
              </div>
            );
          }

          // MEMO: 성능 이슈 vimeo 주소 재생 방지 [https://croquis.slack.com/archives/C066JEHRGQ4/p1715145201815129]
          if (domNode.name === 'iframe' && checkVimeoSource(domNode.attribs.src)) {
            try {
              const originalUrl = domNode.attribs.src;
              const paramsToSet: Record<string, string> = { background: 'true' };
              const convertUrl = modifyUrl(originalUrl, paramsToSet);

              return (
                <LazyIframe
                  source={convertUrl}
                  width={domNode.attribs.width}
                  height={domNode.attribs.height}
                  title='iframe'
                  style={{ ...cssStringToJson(domNode.attribs.style), pointerEvents: 'none' }}
                />
              );
            } catch (e) {
              console.error(e);
              return <></>;
            }
          }

          // MEMO: youtube, vimeo인 경우에는 기존의 영상 비율을 유지하기 위해 스타일 추가
          // MEMO: border 제외 속성 누락된 경우 위해 추가
          if (domNode.name === 'iframe' && checkNeedAdjustSource(domNode.attribs.src)) {
            return adjustAspectRatio(domNode);
          }

          if (domNode.name === 'iframe' && shouldLoadLazyIframe(domNode.attribs.src)) {
            return (
              <LazyIframe
                source={domNode.attribs.src}
                width={domNode.attribs.width}
                height={domNode.attribs.height}
                title='iframe'
              />
            );
          }
          if (domNode.name === 'body') {
            return <div>{domToReact(domNode.children, options)}</div>;
          }

          if (domNode.name === 'table') {
            return <TableTemplate className='TableWrapper'>{domToReact(domNode.children, options)}</TableTemplate>;
          }

          if (checkScriptAttribute(domNode.name)) {
            const src = domNode.attribs.src;
            const isWhiteScript = whiteScriptList.includes(src);

            // MEMO: 인라인 스크립트이거나 허용되지 않은 스크립트 주소일때 필터
            if (!isWhiteScript || !domNode.attribs.src) {
              // eslint-disable-next-line react/jsx-no-useless-fragment
              return <></>;
            }
          }
          return domNode;
        }
      },
    };

    return parse(wrap_description, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, shop_url, isLazyImage]);

  return {
    parseDescription,
  };
}

function cssStringToJson(cssString: string): { [key: string]: string } {
  const stylesArray = cssString.split(';');

  const cssJson: { [key: string]: string } = {};

  stylesArray.forEach((style) => {
    const [property, value] = style.split(':').map((part) => part.trim());

    if (property && value) {
      cssJson[property] = value;
    }
  });

  return cssJson;
}

function modifyUrl(url: string, paramsToSet: Record<string, string>): string {
  try {
    const [baseUrl, queryString] = url.split('?');
    const params = new URLSearchParams(queryString);

    for (const [key, value] of Object.entries(paramsToSet)) {
      params.set(key, value);
    }
    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    console.error('Error modifying URL:', error);
    return url;
  }
}
